(() => {
  if (customElements.get('gp-storage-pdp')) return;
  customElements.define('gp-storage-pdp', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const source = this.querySelector('[data-storage-config]');
      if (!source) return;
      try { this.config = JSON.parse(source.textContent); } catch { return; }
      this.section = this.closest('[id^="shopify-section-"]');
      if (!this.section) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.section.addEventListener('variant:change', event => {
        if (event.detail && Object.hasOwn(event.detail, 'variant')) this.render(event.detail.variant);
      }, options);
      this.section.addEventListener('change', event => {
        if (event.target.closest('hdt-variant-picker')) this.render(null);
      }, { ...options, capture: true });
      this.observer = new MutationObserver(mutations => {
        const picker = this.section.querySelector('hdt-variant-picker');
        const selectedAttribute = mutations.find(mutation => mutation.target === picker && mutation.attributeName === 'selected-variant');
        if (selectedAttribute) {
          this.render(this.findVariant(picker.getAttribute('selected-variant')));
          return;
        }
        const changed = mutations.some(mutation => {
          const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
          if (target?.closest('hdt-variant-picker')) return true;
          return [...mutation.addedNodes, ...mutation.removedNodes].some(node => node.nodeType === 1 && (node.matches('hdt-variant-picker') || node.querySelector('hdt-variant-picker')));
        });
        if (changed) this.sync();
      });
      this.observer.observe(this.section, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['selected-variant'] });
      this.sync();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
    }
    findVariant(id) {
      return this.config.variants.find(variant => String(variant.id) === String(id)) || null;
    }
    sync() {
      const picker = this.section.querySelector('hdt-variant-picker');
      if (!picker) {
        this.render(this.findVariant(this.config.selectedId));
        return;
      }
      const selected = picker.querySelector('#selected-variant');
      if (selected) {
        try { this.render(JSON.parse(selected.textContent)); } catch { this.render(null); }
      } else {
        this.render(this.findVariant(picker.getAttribute('selected-variant')));
      }
    }
    render(variant) {
      const { details, locale, labels } = this.config;
      const valid = variant?.id && this.findVariant(variant.id);
      const entry = valid ? details.variants?.[String(variant.id)] : null;
      const base = details[locale] || details.en || {};
      const localized = entry?.[locale] || {};
      const facts = Object.fromEntries(['size', 'form', 'color', 'summary', 'highlight', 'notice'].map(key => [key, valid ? localized[key] || base[key] || '' : '—']));
      const setText = (selector, value) => this.section.querySelectorAll(selector).forEach(element => {
        if (element.textContent !== value) element.textContent = value;
      });
      for (const key of ['size', 'form', 'color']) setText(`[data-gp-${key}]`, facts[key]);
      setText('[data-storage-highlight]', facts.highlight);
      setText('[data-storage-notice]', facts.notice);
      this.section.querySelectorAll('[data-storage-notice]').forEach(el => {el.hidden = !valid || !facts.notice;});
      this.section.querySelectorAll('hdt-variant-picker fieldset').forEach(field => {
        const index = Number(field.dataset.index);
        field.querySelectorAll('input[type="radio"]').forEach(input => {
          if (!/^DM-GY/.test(input.value)) return;
          const label = field.querySelector('label[for="' + input.id + '"]');
          const item = this.config.variants.find(v => v.options[index] === input.value);
          const entry = item && details.variants?.[String(item.id)]?.[locale];
          if (!label || !entry) return;
          let hint = label.querySelector('.gp-option-size');
          if (!hint) { hint = document.createElement('small'); hint.className = 'gp-option-size'; label.append(hint); }
          const size = entry.size.replace(/^W × D × H: |^闊×深×高：/, '').replace(/^Approx\. |^約/, '').replace(/ \(reference\)$/, '');
          if (hint.textContent !== size) hint.textContent = size;
        });
      });
      const choice = valid ? [facts.form, facts.color].filter(Boolean).join(' · ') : labels.invalid;
      setText('[data-storage-selected]', choice);
      setText('[data-storage-spec]', facts.summary || facts.size);
      const mode = valid ? entry?.mode || 'standard' : 'unavailable';
      this.section.querySelectorAll('[data-storage-mode]').forEach(element => { element.hidden = element.dataset.storageMode !== mode; });
      const delta = entry?.installDelta;
      const hasInstallation = valid && (mode === 'diy' || mode === 'install') && typeof delta === 'number' && Number.isFinite(delta) && delta >= 0;
      this.section.querySelectorAll('[data-storage-install-row]').forEach(element => { element.hidden = !hasInstallation; });
      const amount = hasInstallation ? new Intl.NumberFormat(locale === 'zh' ? 'zh-HK' : 'en-HK', { maximumFractionDigits: 2 }).format(delta) : '';
      setText('[data-storage-install-cost]', hasInstallation ? labels.install_extra.replace('__AMOUNT__', amount) : '');
      setText('[data-storage-install-status]', hasInstallation ? labels[mode === 'install' ? 'install_included' : 'install_optional'] : '');
      if (valid && this.config.productUrl) {
        const productUrl = new URL(this.config.productUrl);
        productUrl.searchParams.set('variant', String(variant.id));
        const assembly = locale === 'zh' ? (mode === 'install' ? '連組裝（只含組裝）' : mode === 'diy' ? '自行組裝' : '標準款') : (mode === 'install' ? 'With assembly (assembly only)' : mode === 'diy' ? 'Self-assembly' : 'Standard');
        const price = new Intl.NumberFormat(locale === 'zh' ? 'zh-HK' : 'en-HK', {style:'currency',currency:'HKD'}).format(variant.price / 100);
        let message = locale === 'zh'
          ? ['你好，我想查詢儲物商品的尺寸及組裝安排。', base.title, choice, assembly + ' · ' + price, productUrl.href, '可用位置（闊 × 深 × 高）：', '門前／開蓋空間及搬運通道：', '我會附上擺放位置及地面／牆身照片。'].join('\n')
          : ['Hello, I would like advice on storage dimensions and assembly.', base.title, choice, assembly + ' · ' + price, productUrl.href, 'Available space (W × D × H):', 'Door/lid clearance and delivery access:', 'I will attach photos of the position and floor/wall.'].join('\n');
        if (details.category === 'cover') message = locale === 'zh'
          ? ['你好，我想查詢保護罩尺寸／訂造。',base.title,facts.size,price,productUrl.href,'傢俬最大闊W × 長L × 高H：','我會附上傢俬擺放照片。'].join('\n')
          : ['Hello, I would like help with cover sizing / a custom cover.',base.title,facts.size,price,productUrl.href,'Maximum furniture width W × length L × height H:','I will attach a photo of the furniture arrangement.'].join('\n');
        const inquiryUrl = 'https://wa.me/85262852233?text=' + encodeURIComponent(message);
        document.querySelectorAll('[data-storage-inquiry]').forEach(link => { link.href = inquiryUrl; });
      }
    }
  });
})();
